import { Alert, Modal, Skeleton } from "@hakistudio/hakiui";
import {
  formatAmount,
  STORAGE_LOCATION_LABELS,
  type StorageLocation,
} from "@sebas/core";
import { Check, Minus, Plus, Refrigerator, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Field";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import {
  useAddFridgeItem,
  useDeleteFridgeItem,
  useFridge,
  useUpdateFridgeItem,
  type PantryItem,
} from "@/lib/pantry";
import { ExpiryBadge, expiryDays } from "./ExpiryBadge";
import {
  FRIDGE_UNIT_OPTIONS,
  LocationSegmented,
  parseQuantity,
  QuantityFields,
  type AmountDraft,
} from "./UnitFields";

type Draft = AmountDraft & { name: string; location: StorageLocation; expiresAt: string };

const emptyDraft: Draft = { name: "", quantity: "", unit: "", location: "fridge", expiresAt: "" };

const draftOf = (item: PantryItem): Draft => ({
  name: item.name,
  quantity: item.quantity === null ? "" : String(item.quantity),
  unit: item.unit,
  location: item.location,
  expiresAt: item.expiresAt ?? "",
});

const toPayload = (draft: Draft) => ({
  name: draft.name.trim(),
  quantity: parseQuantity(draft.quantity),
  unit: draft.unit,
  location: draft.location,
  expiresAt: draft.expiresAt || null,
});

/** A whole-number-ish step that suits the unit: grams and millilitres move in tens. */
const stepFor = (unit: string) => (unit === "g" || unit === "ml" ? 10 : 1);

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Local date, `yyyy-mm-dd`, `days` from today — for the expiry shortcut chips. */
const isoInDays = (days: number) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

const EXPIRY_CHIPS = [
  { label: "3 days", days: 3 },
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
];

type Filter = StorageLocation | "all";

/** Soonest expiry first (undated last), then alphabetical — what to use up rises to the top. */
const byUrgency = (a: PantryItem, b: PantryItem) => {
  const da = expiryDays(a.expiresAt);
  const db = expiryDays(b.expiresAt);
  if (da !== db) {
    if (da === null) return 1;
    if (db === null) return -1;
    return da - db;
  }
  return a.name.localeCompare(b.name);
};

/**
 * The fridge.
 *
 * One inventory per account, so this panel shows the same rows in every fridge
 * note — the note is a way in, not a container. It is built to be lived in from
 * a phone: the add form stays out of the way until you want it, rows open on a
 * tap anywhere, and a running total takes one tap to nudge up or down.
 */
export function FridgePanel({
  teamId = null,
  canEdit,
}: {
  teamId?: string | null;
  canEdit: boolean;
}) {
  const { data: items, isPending } = useFridge(teamId);
  const update = useUpdateFridgeItem();
  const remove = useDeleteFridgeItem();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");

  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : String(e));

  // Stepper taps land instantly on screen and are saved together a beat later.
  const [nudged, setNudged] = useState<Record<string, number>>({});
  const queued = useRef<Map<string, number>>(new Map());
  const dropNudge = (id: string) =>
    setNudged((n) => {
      if (!(id in n)) return n;
      const rest = { ...n };
      delete rest[id];
      return rest;
    });
  const flushNudges = useDebouncedCallback(() => {
    for (const [id, quantity] of queued.current) {
      update.mutate({ id, quantity }, { onError: fail, onSettled: () => dropNudge(id) });
    }
    queued.current.clear();
  }, 600);

  const startEdit = (item: PantryItem) => {
    setAdding(false);
    setEditing(item.id);
    setEditDraft(draftOf(item));
    setError(null);
  };

  const saveEdit = (id: string) => {
    if (!editDraft.name.trim()) return;
    update.mutate(
      { id, ...toPayload(editDraft) },
      { onSuccess: () => setEditing(null), onError: fail },
    );
  };

  const nudge = (item: PantryItem, delta: number) => {
    const step = stepFor(item.unit);
    // The ref is current within the same tick, so a fast run of taps still adds up.
    const base = queued.current.get(item.id) ?? nudged[item.id] ?? item.quantity ?? 0;
    const next = Math.max(0, round2(base + delta * step));
    setNudged((n) => ({ ...n, [item.id]: next }));
    queued.current.set(item.id, next);
    flushNudges.run();
  };

  const all = items ?? [];
  const rows = [...all]
    .filter((i) => filter === "all" || i.location === filter)
    .sort(byUrgency);
  const soon = all.filter((i) => {
    const d = expiryDays(i.expiresAt);
    return d !== null && d <= 3;
  }).length;

  return (
    <section className="flex flex-col gap-4 rounded-card border border-line bg-raised p-4 sm:p-5">
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-tile-mint text-tile-ink">
            <Refrigerator className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold leading-tight text-ink">
              In the fridge
            </h2>
            <p className="text-xs text-ink-faint">
              {all.length === 0
                ? "Shared with your recipes"
                : `${all.length} ${all.length === 1 ? "item" : "items"} · shared with your recipes`}
            </p>
          </div>
          {canEdit ? (
            <Button
              className="ml-auto shrink-0"
              size="sm"
              onClick={() => {
                setEditing(null);
                setAdding(true);
              }}
            >
              <Plus className="h-4 w-4" /> New item
            </Button>
          ) : null}
        </div>

        {soon > 0 ? (
          <p className="rounded-2xl bg-tile-coral/15 px-3 py-2 text-xs font-medium text-tile-coral">
            {soon} {soon === 1 ? "item" : "items"} to use up in the next few days
          </p>
        ) : null}

        {all.length > 0 ? (
          <div className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-0.5">
            {(["all", "fridge", "freezer", "pantry"] as Filter[]).map((f) => {
              const count =
                f === "all" ? all.length : all.filter((i) => i.location === f).length;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  aria-pressed={filter === f}
                  className={cn(
                    "shrink-0 rounded-full px-3 py-1 text-xs font-medium transition",
                    filter === f
                      ? "bg-accent text-accent-ink"
                      : "bg-sunken text-ink-faint hover:text-ink",
                  )}
                >
                  {f === "all" ? "All" : STORAGE_LOCATION_LABELS[f]}
                  <span className="ml-1 tabular-nums opacity-60">{count}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </header>

      {adding && canEdit ? (
        <AddItemModal teamId={teamId} onClose={() => setAdding(false)} />
      ) : null}

      {error ? <Alert variant="danger" title={error} onClose={() => setError(null)} /> : null}

      {isPending ? (
        <Skeleton height="4rem" radius="lg" />
      ) : all.length === 0 ? (
        <p className="rounded-2xl bg-sunken px-4 py-6 text-center text-sm text-ink-faint">
          Nothing in yet. Add what you have and your recipes will start checking against it.
        </p>
      ) : rows.length === 0 ? (
        <p className="px-1 text-sm text-ink-faint">Nothing in the {filter}.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((item) => {
            const days = expiryDays(item.expiresAt);
            const gone = days !== null && days < 0;
            const qty = nudged[item.id] ?? item.quantity;

            if (editing === item.id) {
              return (
                <li
                  key={item.id}
                  className="flex flex-col gap-3 rounded-2xl bg-sunken p-3 ring-1 ring-line"
                >
                  <Input
                    size="md"
                    aria-label="Item name"
                    value={editDraft.name}
                    onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <QuantityFields
                      draft={editDraft}
                      size="md"
                      onChange={(a) => setEditDraft({ ...editDraft, ...a })}
                      unitOptions={FRIDGE_UNIT_OPTIONS}
                    />
                  </div>
                  <LocationSegmented
                    className="w-full"
                    value={editDraft.location}
                    onChange={(location) => setEditDraft({ ...editDraft, location })}
                  />
                  <Input
                    className="w-40"
                    size="sm"
                    type="date"
                    aria-label="Use-by date"
                    value={editDraft.expiresAt}
                    onChange={(e) => setEditDraft({ ...editDraft, expiresAt: e.target.value })}
                  />
                  <div className="flex items-center gap-2">
                    <Button onClick={() => saveEdit(item.id)} disabled={update.isPending}>
                      <Check className="h-4 w-4" /> Save
                    </Button>
                    <Button variant="ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${item.name}`}
                      className="ml-auto text-tile-coral"
                      onClick={() =>
                        remove.mutate(item.id, {
                          onSuccess: () => setEditing(null),
                          onError: fail,
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            }

            return (
              <li
                key={item.id}
                className={cn(
                  "flex items-stretch gap-1 overflow-hidden rounded-2xl bg-sunken",
                  gone && "opacity-60",
                )}
              >
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => canEdit && startEdit(item)}
                  className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left disabled:cursor-default"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">
                      {item.name}
                    </span>
                    <span className="text-xs text-ink-faint">
                      {qty === null
                        ? "Some"
                        : formatAmount({ quantity: qty, unit: item.unit }) || "Some"}
                      {" · "}
                      {STORAGE_LOCATION_LABELS[item.location]}
                    </span>
                  </span>
                  <ExpiryBadge expiresAt={item.expiresAt} />
                </button>

                {canEdit && qty !== null ? (
                  <div className="flex shrink-0 items-center gap-0.5 pr-1.5">
                    <button
                      type="button"
                      aria-label={`Less ${item.name}`}
                      disabled={qty <= 0}
                      onClick={() => nudge(item, -1)}
                      className="grid h-8 w-8 place-items-center rounded-full text-ink transition hover:bg-raised disabled:opacity-30"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="min-w-8 text-center text-sm font-medium tabular-nums text-ink">
                      {qty}
                    </span>
                    <button
                      type="button"
                      aria-label={`More ${item.name}`}
                      onClick={() => nudge(item, 1)}
                      className="grid h-8 w-8 place-items-center rounded-full text-ink transition hover:bg-raised"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Adding an item happens in a modal so the whole screen backs it: on a phone
 * that means the keyboard, the name field and nothing else to look at. It stays
 * open after each add and lists what has gone in, so a bag of shopping is one
 * sitting rather than one trip through the panel per item.
 */
function AddItemModal({
  teamId,
  onClose,
}: {
  teamId: string | null;
  onClose: () => void;
}) {
  const add = useAddFridgeItem(teamId);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [addedNames, setAddedNames] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const nameField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => nameField.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name) return;
    setError(null);
    add.mutate(toPayload(draft), {
      onSuccess: () => {
        setAddedNames((n) => [name, ...n]);
        // Keep location, clear the rest — the next item is usually the same shelf.
        setDraft({ ...emptyDraft, location: draft.location });
        nameField.current?.focus();
      },
      onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Add to the fridge">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input
          ref={nameField}
          size="md"
          placeholder="What did you put in?"
          aria-label="Item name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />

        <div className="flex flex-wrap items-center gap-2">
          <QuantityFields
            draft={draft}
            size="md"
            onChange={(a) => setDraft({ ...draft, ...a })}
            unitOptions={FRIDGE_UNIT_OPTIONS}
          />
        </div>

        <LocationSegmented
          className="w-full"
          value={draft.location}
          onChange={(location) => setDraft({ ...draft, location })}
        />

        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-ink-faint">Use by</span>
          <div className="flex flex-wrap items-center gap-2">
            {EXPIRY_CHIPS.map((c) => {
              const value = isoInDays(c.days);
              return (
                <button
                  key={c.days}
                  type="button"
                  onClick={() =>
                    setDraft({ ...draft, expiresAt: draft.expiresAt === value ? "" : value })
                  }
                  className={cn(
                    "rounded-full px-3 py-1.5 text-xs font-medium transition",
                    draft.expiresAt === value
                      ? "bg-accent text-accent-ink"
                      : "bg-sunken text-ink-faint hover:text-ink",
                  )}
                >
                  {c.label}
                </button>
              );
            })}
            <Input
              className="w-40"
              size="sm"
              type="date"
              aria-label="Use-by date"
              value={draft.expiresAt}
              onChange={(e) => setDraft({ ...draft, expiresAt: e.target.value })}
            />
          </div>
        </div>

        {error ? <Alert variant="danger" title={error} /> : null}

        {addedNames.length > 0 ? (
          <p className="text-xs text-ink-faint">
            Added: <span className="text-ink">{addedNames.join(" · ")}</span>
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={add.isPending || !draft.name.trim()}>
            <Plus className="h-4 w-4" /> Add item
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {addedNames.length > 0 ? "Done" : "Cancel"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
